import { useQuery } from '@tanstack/react-query';

import { api, type ApiRequestConfig } from 'api/v1/api';
import { isAuthSessionActive, useAuthSession } from 'hooks/useAuthSession';

export type VerificationItemDecision = {
  kind?: string;
  education_index?: number;
  status?: string;
  comment?: string;
  reviewed_at?: string;
};

export type RejectedVerificationItem = {
  key: string;
  kind: string;
  education_index?: number | null;
  status: string;
  comment?: string | null;
  reviewed_at?: string | null;
};

export type VerificationStatusResponse = {
  profile: string;
  photo: string;
  passport: string;
  reviews_verified: number;
  reviews_total: number;
  overall: string;
  can_be_found: boolean;
  is_public: boolean;
  verification_comment?: string | null;
  verification_request_status?: string | null;
  item_decisions?: Record<string, VerificationItemDecision>;
  rejected_items?: RejectedVerificationItem[];
  pending_edits?: boolean;
  can_revert?: boolean;
  draft_changes?: {
    field?: string;
    label: string;
    change_type?: string;
    before?: string | null;
    after?: string | null;
    after_url?: string | null;
    field_changes?: { field?: string; change_type?: string }[];
  }[];
  declined_changes?: Omit<RejectedVerificationItem, "status">[];
  questionnaire?: {
    steps: { key: string; title: string; done: boolean }[];
    ready_to_submit?: boolean;
    moderation?: { status: string; label: string };
  } | null;
};

const QUERY_KEY = ['verification-status'];

export const useVerificationEdits = (enabled = true) => {
  const { data: session } = useAuthSession();

  const query = useQuery<VerificationStatusResponse>({
    queryKey: QUERY_KEY,
    queryFn: () =>
      api
        .get('/profile/verification-status', {
          skipGlobalErrorHandler: true,
        } as ApiRequestConfig)
        .then((response) => response.data),
    enabled: enabled && isAuthSessionActive(session),
    staleTime: 30_000,
  });

  const rejectedItems = query.data?.rejected_items ?? [];
  const hasRejectedItems = rejectedItems.length > 0;

  return {
    ...query,
    rejectedItems,
    hasRejectedItems,
    itemDecisions: query.data?.item_decisions ?? {},
    declinedChanges: query.data?.declined_changes ?? [],
    pendingEdits: Boolean(query.data?.pending_edits),
    canRevert: Boolean(query.data?.can_revert),
    draftChanges: query.data?.draft_changes ?? [],
    verificationComment: query.data?.verification_comment ?? null,
    profileStatus: query.data?.profile ?? 'draft',
  };
};
